import React from "react";
import { atom } from "jotai";

import { GeneralInfo } from "pages/Event/events/TradeToEarnEvent/types";
import tradeIcon from "img/TradeIconEvent.png";
import earnIcon from "img/EarnIconEvent.png";
import convertIcon from "img/ConvertIconEvent.png";
import {
  getCurrentEpocheIndex,
  getEpocheEndDay,
  getNextRewardDistributionDay,
} from "pages/Event/events/TradeToEarnEvent/utils/epocheDates";

import parse from "parse-duration";
import { EventCountdown } from "pages/Event/components/EventCountdown";

export const isCollapsedAtom = atom(false);

type Props = React.ComponentProps<"div"> & {
  generalInfo: GeneralInfo;
};

export const AboutSection = React.memo((props: Props) => {
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const { generalInfo } = props;

  const currentDate = new Date();
  const currentDay = Math.floor(parse(`${currentDate.valueOf()} ms`, "day") ?? 0) as number;
  const nextEpocheStartDate = React.useMemo(() => {
    if (currentDay < generalInfo.tier1StartIndex.toNumber()) {
      return new Date(generalInfo.tier1StartIndex.toNumber());
    }
    if (currentDay >= generalInfo.endIndex.toNumber()) {
      return null;
    }

    const date = new Date(parse(`${getEpocheEndDay(currentDay)} day`, "ms") ?? 0);

    // Get month name
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthName = monthNames[date.getMonth()];

    // Format the date
    const formattedDate = `${monthName} ${date.getDate()} ${date.getFullYear()} ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}: GMT+${date.getTimezoneOffset() / -60}`;

    return formattedDate;
  }, [currentDay, generalInfo.endIndex, generalInfo.tier1StartIndex]);

  const currentEpocheIndex = React.useMemo(() => {
    return getCurrentEpocheIndex({
      currentDay,
      generalInfo,
    });
  }, [currentDay, generalInfo]);

  const nextRewardDistributionDate = React.useMemo(() => {
    const day = getNextRewardDistributionDay({
      currentDay,
      generalInfo,
    });
    if (!day) {
      return null;
    }
    return new Date(parse(`${day} day`, "ms") ?? 0);
  }, [currentDay, generalInfo]);

  return (
    <div
      className="mt-[3rem] bg-main p-[0.5rem] md:p-[2rem] rounded-[2.5rem] border border-solid min-h-[calc(3rem+2rem*2+2px)] @container/about-section w-full md:w-2/3 flex flex-col"
      ref={sectionRef}
    >
      <div className="flex flex-col md:flex-row w-full gap-5 ">
        <div className="flex flex-col w-full gap-5 justify-center md:justify-start text-center md:text-start">
          <p className="text-black text-[2.4rem] leading-[1.4] font-semibold	">Epoch Details</p>
          <div className="w-full text-center md:text-start flex flex col  items-center md:items-start gap-2 text-white bg-[#101014] h-[10rem] rounded-[2rem] p-[2rem] flex-col">
            {nextEpocheStartDate && (
              <div className="leading-[1.4] md:text-[1.4rem] text-[1.3rem]">
                Next Epoch: {nextEpocheStartDate.toString()}
              </div>
            )}

            {currentEpocheIndex !== null && (
              <p className="text-[2.4rem] leading-[1.4] font-medium">Current Epoch #{currentEpocheIndex + 1}</p>
            )}
          </div>
        </div>
        <div className="text-black w-full mt-[0rem] md:mt-[5rem]">
          {nextRewardDistributionDate && (
            <EventCountdown countdownEndTimestamp={nextRewardDistributionDate.valueOf()} />
          )}
        </div>
      </div>
      <div className="flex flex-row w-full mt-[2rem]">
        <div className="flex flex-col w-full gap-5 text-center md:text-start">
          <p className="text-black text-[2.4rem] leading-[1.4] font-semibold">How to participate</p>
          <div className="w-full flex flex-col md:flex-row items-start text-start gap-[1rem] text-white bg-[#101014] h-full md:h-[10rem] rounded-[2rem] p-[2rem]">
            <div className="flex flex-row items-center gap-[1rem]">
              <img src={tradeIcon} alt="trade" className="w-[5rem]" />
              <div className="flex flex-col text-[1.2rem]">
                <p className="text-[1.4rem]">Trade</p>
                <p>Trade long or short in your favorite market. </p>
              </div>
            </div>
            <div className="flex flex-row items-center gap-[1rem]">
              <img src={earnIcon} alt="trade" className="w-[5rem]" />
              <div className="flex flex-col text-[1.2rem]">
                <p className="text-[1.4rem]">Earn</p>
                <p>Gain Trading Points for every $1 trade.</p>
              </div>
            </div>
            <div className="flex flex-row items-center gap-[1rem]">
              <img src={convertIcon} alt="trade" className="w-[5rem]" />
              <div className="flex flex-col text-[1.2rem]">
                <p className="text-[1.4rem]">Convert</p>
                <p>TP gets automatically converted to FOX.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
